/**
 * 分類マスター詳細の欄 1 つの値と、欄名の表示名。マスター詳細の本体とステージの欄の両方が使う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの欄の値（本文・参照・選択肢・未設定）と欄名の表示名を描き分ける共通部。
 * @graph-connects TermText [calls] 本文の用語自動リンク
 */
import type { MasterDetailOutput } from '@cortex/domain-spec-console';
import { useTranslation } from 'react-i18next';

import { TermText } from '../components/spec/TermText.js';

/** マスターの欄 1 つ。 @graph-connects none */
export type MasterField = MasterDetailOutput['fields'][number];

/**
 * 欄 1 つの値。出し方に応じて本文・参照先の表示名・選択肢の並びを描く。
 *
 * @param props 欄 1 つ
 * @returns 欄の値
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの欄の値を、出し方（本文 / 参照 / 選択肢）に応じて描く。
 * @graph-connects UnsetValue [calls] 値の無い欄
 * @graph-connects TermText [calls] 本文の用語自動リンク
 */
export function MasterFieldValue({ field }: { field: MasterField }) {
  const { t } = useTranslation();

  if (field.kind === 'token' || field.kind === 'tokens') {
    if (field.tokens.length === 0) return <UnsetValue />;
    /* 動詞の層は動詞の一覧と同じ訳語で出す。原語のまま出すと、一覧と詳細で同じ値の見え方が違う。 */
    const tokens =
      field.field === 'layer'
        ? field.tokens.map((token) => t(`verbLayer.${token}`, { defaultValue: token }))
        : field.tokens;
    return <span>{tokens.join(t('masterDetail.tokenSeparator'))}</span>;
  }
  if (field.text === '') return <UnsetValue />;
  return <TermText text={field.text} />;
}

/**
 * 値の無い欄の表示。**空欄にしない。**
 *
 * 空白で出すと、読み手は取得の失敗と未設定を見分けられない。
 *
 * @returns 「未設定」
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 値の無い欄を「未設定」と描く。
 * @graph-connects none
 */
export function UnsetValue() {
  const { t } = useTranslation();

  return <span className="text-[var(--color-text-muted)]">{t('masterDetail.unset')}</span>;
}

/**
 * 契約の欄名から表示名の翻訳キーを引く。
 *
 * 入れ子の欄（`exit.business`）は綴りの区切りを落として 1 語にする。翻訳リソースの
 * キーはドットで階層を表すので、欄名のドットをそのまま渡すと存在しない階層を引きにいく。
 *
 * @param field 契約の欄名
 * @returns 表示名の翻訳キー
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの欄名から表示名の翻訳キーを引く。
 * @graph-connects none
 */
export function fieldLabelKey(field: string): string {
  const [head, ...rest] = field.split('.');
  const joined = [head, ...rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1))].join(
    '',
  );
  return `masterDetail.field.${joined}`;
}
